import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { numericFilterColumn } from '../../../utils/numericFilterColumn';
import PersonCell from '../../../components/ui/PersonCell';

const StatusCell = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

// "Erişim Kapatıldı" tek başına "iş bitti" gibi okunuyordu; oysa revoked satır
// hâlâ doğrulama bekler ve süreç onun yüzünden ilerlemez (müşteri geri
// bildirimi). Rozet bu ara durumu her iki yüzeyde de (müstakil sayfa + süreç
// detayı sekmesi) görünür kılar.
const AwaitingBadge = styled.span`
  font-size: 11px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  padding: 2px 8px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.auxiliar.warning};
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
`;

export const ACCESS_CATEGORIES = [
  'email', 'vpn', 'crm', 'finance', 'project_mgmt', 'code_repo',
  'intranet', 'cloud_storage', 'physical', 'social', 'other',
];
export const STATUSES = ['pending', 'revoked', 'verified', 'blocked'];

const multiSelectFilter = (label, options, valueRender) => ({
  filter: true,
  sort: true,
  filterType: 'custom',
  filterOptions: {
    defaultOp: 'in',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0]
        ? filterList[index][0].split(',').filter(Boolean)
        : [];
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
              filterList[index] = csv ? [csv] : [];
              onChange(filterList[index], index, column);
            }}
            renderValue={(selected) => selected.map((s) => valueRender ? valueRender(s) : s).join(', ')}
          >
            {options.map((o) => (
              <MenuItem key={o} value={o}>{valueRender ? valueRender(o) : o}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  },
});

const useOffboardingItAccessApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 25, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const merged = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : (lastValidQueryParams.current || { pageNumber: 1, pageSize: 25 });
    lastValidQueryParams.current = merged;
    setFetching(true);
    api.get(`/v1/offboarding-it-access?${new URLSearchParams(merged)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 25,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const revoke = useCallback(async (id, log) => {
    try { await api.post(`/v1/offboarding-it-access/${id}/revoke`, { verificationLog: log }); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const verify = useCallback(async (id, log) => {
    try { await api.post(`/v1/offboarding-it-access/${id}/verify`, { verificationLog: log }); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const block = useCallback(async (id, reason) => {
    try { await api.post(`/v1/offboarding-it-access/${id}/block`, { reason }); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const columns = useMemo(() => {
    const categoryLabel = (s) => t(`offboardingItAccess.category.${s}`, { defaultValue: s });
    const statusLabel = (s) => t(`offboardingItAccess.status.${s}`, { defaultValue: s });

    return [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      { name: 'offboardingId', label: 'OD', options: { filter: false, sort: false, display: 'excluded' } },
      {
        name: 'personFullName',
        label: t('offboardingItAccess.person'),
        options: {
          customBodyRender: (v, tableMeta) => (
            <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.personProfilePicture} />
          ),
          filter: true, sort: true,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like', filterKey: 'personName[like]' },
        },
      },
      {
        name: 'systemName',
        label: t('offboardingItAccess.systemName'),
        options: {
          filter: true, sort: true,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like' },
        },
      },
      {
        name: 'accessCategory',
        label: t('offboardingItAccess.categoryLabel'),
        options: {
          ...multiSelectFilter(t('offboardingItAccess.categoryLabel'), ACCESS_CATEGORIES, categoryLabel),
          customBodyRender: (v) => categoryLabel(v),
        },
      },
      {
        name: 'status',
        label: t('offboardingItAccess.statusLabel'),
        options: {
          ...multiSelectFilter(t('offboardingItAccess.statusLabel'), STATUSES, statusLabel),
          customBodyRender: (v) => (v === 'revoked'
            ? (
              <StatusCell>
                {statusLabel(v)}
                <AwaitingBadge>{t('offboardingItAccess.awaitingVerification')}</AwaitingBadge>
              </StatusCell>
            )
            : statusLabel(v)),
        },
      },
      numericFilterColumn('priority', t('offboardingItAccess.priority'), { decimals: 0 }),
      dateFilterColumn('slaCloseAt', t('offboardingItAccess.slaCloseAt'), lang),
      dateFilterColumn('revokedAt', t('offboardingItAccess.revokedAt'), lang),
      dateFilterColumn('verifiedAt', t('offboardingItAccess.verifiedAt'), lang),
    ];
  }, [t, lang, data]);

  return { fetching, data, pagination, columns, refresh, revoke, verify, block };
};

export default useOffboardingItAccessApi;
